import React, { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import {
  capitalizeFirstLetter,
  formatPhoneNumber,
} from "../../../../utils/Functions";
import moment from "moment";
import { MdCall, MdContentCopy, MdCheck } from "react-icons/md";
import {
  getPartner,
  getVisitReceiptDetails,
} from "../../../../Service/partnerAPI";
import {
  buildReceiptSections,
  getPhysioSessionLabel,
  numberToIndianCurrencyWords,
  shouldShowReceiptForStatus,
} from "./patientReceiptUtils";

const formatLabelToken = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const normalizeDutyTypeToken = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const parseShiftType = (shiftType = "") => {
  const parts = String(shiftType || "")
    .split(",")
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  return {
    shift: String(parts[0] || "").toLowerCase(),
    hours: String(parts[1] || "").toUpperCase(),
    parts,
  };
};

const getDutyTypeDisplay = (visit) => {
  if (!visit) return "-";
  const role = String(visit?.specialistInfo?.role || "")
    .trim()
    .toLowerCase();
  const dutyTypeRaw = String(visit?.dutyType || "").trim();
  const dutyType = normalizeDutyTypeToken(dutyTypeRaw);

  if (role === "physiotherapist") return "Session";

  if (
    role === "nurse" ||
    role === "nursing-assistant" ||
    role === "nursing assistant" ||
    role === "nursingassistant"
  ) {
    if (dutyType === "onetime") return "One Time";

    if (dutyType === "regular") {
      const { shift, hours } = parseShiftType(
        visit?.visitInfo?.shiftType || "",
      );
      if (shift && hours) return `${formatLabelToken(shift)}, ${hours}`;
      if (shift) return formatLabelToken(shift);
      return "Regular";
    }
  }

  if (dutyType === "onetime") return "One Time";

  const shiftTypeRaw = String(visit?.visitInfo?.shiftType || "").trim();
  if (shiftTypeRaw && dutyTypeRaw) return `${shiftTypeRaw}, ${dutyTypeRaw}`;
  if (shiftTypeRaw) return shiftTypeRaw;
  if (dutyTypeRaw) return dutyTypeRaw;
  return "-";
};

const PatientHistory = ({
  selectedPatient,
  dischargeSummaries = [],
  dischargeSummariesLoading = false,
  dischargeSummariesError = "",
  hideBookingInfo = false,
  showDutyType = false,
  showPatientListDetails = false,
}) => {
  const [showMap, setShowMap] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");
  const [receiptPartner, setReceiptPartner] = useState(null);
  const [receiptPaymentDetailsByKey, setReceiptPaymentDetailsByKey] = useState(
    {},
  );
  const [receiptSegmentErrors, setReceiptSegmentErrors] = useState({});
  const [activeReceiptKey, setActiveReceiptKey] = useState("");
  const [copiedId, setCopiedId] = useState(false);

  const handleCopyId = () => {
    const jobIDToCopy = selectedPatient?.jobID;
    if (jobIDToCopy) {
      navigator.clipboard.writeText(jobIDToCopy);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  // Close map when patient changes (prevents stale modal)
  useEffect(() => {
    setShowMap(false);
    setShowReceiptModal(false);
    setReceiptError("");
    setReceiptPartner(null);
    setReceiptPaymentDetailsByKey({});
    setReceiptSegmentErrors({});
    setActiveReceiptKey("");
  }, [selectedPatient?._id]);

  const headerColor = "#546bb0";

  const panelHeightClass = "h-auto min-[1100px]:h-full ";

  // Prefer visit address first (most accurate for current booking), fallback to patient profile address
  const visitAddressInfo = selectedPatient?.visitInfo?.addressInfo;
  const profileAddressInfo =
    selectedPatient?.patientInfo?.personalInfo?.addressInfo;
  const addressInfo = visitAddressInfo || profileAddressInfo || {};

  const lat = addressInfo?.coordinates?.lat;
  const lng = addressInfo?.coordinates?.lng;

  const googleMapUrl =
    lat && lng
      ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
      : "";

  const services = selectedPatient?.visitInfo?.services || [];

  // phone: handle multiple shapes (relationship vs relation + nested otherInfo)
  const relationship = String(
    selectedPatient?.patient?.relationship ||
      selectedPatient?.patient?.relation ||
      "",
  )
    .trim()
    .toLowerCase();

  const jobID = selectedPatient?.jobID;

  const totalDutyDays = selectedPatient?.visitInfo?.days;

  const phone =
    relationship === "self"
      ? selectedPatient?.patientInfo?.phone ||
        selectedPatient?.patient?.phone ||
        selectedPatient?.patientInfo?.personalInfo?.basicInfo?.otherInfo
          ?.phone ||
        ""
      : selectedPatient?.patientInfo?.personalInfo?.basicInfo?.otherInfo
          ?.phone ||
        selectedPatient?.patientInfo?.phone ||
        selectedPatient?.patient?.phone ||
        "";

  const specialistRole = String(
    selectedPatient?.specialistInfo?.role || "",
  ).toLowerCase();
  const dutyType = String(selectedPatient?.dutyType || "").toLowerCase();
  const rawIsAssessment = selectedPatient?.isAssessment;

  const isAssessmentPhysioVisit =
    specialistRole === "physiotherapist" &&
    (rawIsAssessment === true ||
      String(rawIsAssessment).toLowerCase() === "true");

  const isNonAssessmentPhysioVisit =
    specialistRole === "physiotherapist" &&
    (rawIsAssessment === false ||
      String(rawIsAssessment).toLowerCase() === "false");

  const isNurseOneTimeVisit =
    specialistRole === "nurse" && dutyType === "onetime";

  const isNurseRegularVisit =
    specialistRole === "nurse" && dutyType === "regular";

  const orderServiceFees = Array.isArray(
    selectedPatient?.paymentInfo?.serviceFees,
  )
    ? selectedPatient.paymentInfo.serviceFees
    : [];

  const orderAmountServiceTotal = orderServiceFees.reduce(
    (sum, item) => sum + Number(item?.fee ?? item?.fees ?? 0),
    0,
  );
  const orderAmountTotalDays = Number(
    selectedPatient?.paymentInfo?.totalDays ??
      selectedPatient?.visitInfo?.days ??
      0,
  );
  const orderAmountTotal = Number(
    selectedPatient?.paymentInfo?.totalFees ??
      (orderAmountTotalDays > 0
        ? orderAmountServiceTotal * orderAmountTotalDays
        : orderAmountServiceTotal),
  );

  const nurseFeePerDay = Number(selectedPatient?.paymentInfo?.feePerDay ?? 0);
  const nurseTotalFees = Number(selectedPatient?.paymentInfo?.totalFees ?? 0);
  const nurseTotalDays = Number(selectedPatient?.paymentInfo?.totalDays ?? 0);
  const normalizedPaymentMode = String(selectedPatient?.paymentMode || "")
    .trim()
    .toLowerCase();
  const orderPaymentType =
    normalizedPaymentMode === "daily_qr"
      ? "Daily Payment"
      : normalizedPaymentMode === "location_qr"
        ? "Upfront (QR)"
        : normalizedPaymentMode === "online"
          ? "Upfront (Online)"
          : "Upfront Payment";

  const physioAssessmentFee = Number(
    selectedPatient?.paymentInfo?.assessment?.fee ?? 0,
  );
  const physioSessions = Array.isArray(selectedPatient?.paymentInfo?.sessions)
    ? selectedPatient.paymentInfo.sessions
    : [];

  const physioExtensionTotal = physioSessions.reduce(
    (sum, session) =>
      sum + Number(session?.extensionFees ?? session?.grandTotal ?? 0),
    0,
  );
  const physioAssessmentTotal = physioAssessmentFee + physioExtensionTotal;

  const preExistingConditionsData =
    selectedPatient?.patientInfo?.preExistingConditions?.completed &&
    selectedPatient?.patientInfo?.preExistingConditions?.data?.length > 0
      ? selectedPatient.patientInfo.preExistingConditions.data
      : [];

  const allergiesData =
    selectedPatient?.patientInfo?.allergies?.completed &&
    selectedPatient?.patientInfo?.allergies?.data?.length > 0
      ? selectedPatient.patientInfo.allergies.data
      : [];

  const medicalHistoryData =
    selectedPatient?.patientInfo?.medicalHistory?.completed &&
    selectedPatient?.patientInfo?.medicalHistory?.data?.length > 0
      ? selectedPatient.patientInfo.medicalHistory.data
      : [];

  const familyHistoryData =
    selectedPatient?.patientInfo?.familyHistory?.completed &&
    selectedPatient?.patientInfo?.familyHistory?.data?.length > 0
      ? selectedPatient.patientInfo.familyHistory.data
      : [];

  const fullAddress = useMemo(() => {
    const addr = [
      addressInfo?.address1,
      addressInfo?.address2,
      addressInfo?.district,
      addressInfo?.city,
      addressInfo?.state,
      addressInfo?.pincode,
    ]
      .filter(Boolean)
      .join(", ");
    return addr || "";
  }, [addressInfo]);

  const hasAnyData = Boolean(selectedPatient);
  const patientListMrn = selectedPatient?.patientListDetails?.mrn || "-";
  const patientListLastPrescribedAt =
    selectedPatient?.patientListDetails?.lastPrescribedAt;
  const patientListLastPrescribedMoment = patientListLastPrescribedAt
    ? moment(patientListLastPrescribedAt)
    : null;
  const patientListLastPrescribed = patientListLastPrescribedMoment?.isValid()
    ? patientListLastPrescribedMoment.format("DD MMM YYYY")
    : "-";

  const hasPatientHistoryData =
    preExistingConditionsData.length > 0 ||
    allergiesData.length > 0 ||
    medicalHistoryData.length > 0 ||
    familyHistoryData.length > 0;

  const receiptSections = useMemo(
    () => buildReceiptSections(selectedPatient),
    [selectedPatient],
  );
  const showReceiptAction =
    shouldShowReceiptForStatus(selectedPatient?.status) &&
    receiptSections.length > 0;
  const activeReceiptSection = useMemo(() => {
    if (!receiptSections.length) return null;
    return (
      receiptSections.find((section) => section.key === activeReceiptKey) ||
      receiptSections[0]
    );
  }, [activeReceiptKey, receiptSections]);
  const receiptPaymentDetails = useMemo(() => {
    if (!activeReceiptSection?.key) return null;
    return receiptPaymentDetailsByKey[activeReceiptSection.key] || null;
  }, [activeReceiptSection, receiptPaymentDetailsByKey]);
  const activeReceiptError = activeReceiptSection?.key
    ? receiptSegmentErrors[activeReceiptSection.key] || ""
    : "";
  const receiptLineItems = activeReceiptSection?.lineItems || [];
  const receiptTotalAmount = activeReceiptSection?.totalAmount || 0;
  const receiptAmountInWords = useMemo(
    () => numberToIndianCurrencyWords(receiptTotalAmount),
    [receiptTotalAmount],
  );
  const receiptDocumentLabel = activeReceiptSection?.title || "Receipt";
  const receiptPartnerId = useMemo(() => {
    const rawPartnerID =
      selectedPatient?.appointmentType?.partnerID ||
      selectedPatient?.partnerID ||
      null;

    if (!rawPartnerID) return "";
    if (typeof rawPartnerID === "string") return rawPartnerID;
    if (typeof rawPartnerID === "object") {
      return String(
        rawPartnerID?._id || rawPartnerID?.id || rawPartnerID?.$oid || "",
      );
    }
    return String(rawPartnerID);
  }, [selectedPatient?.appointmentType?.partnerID, selectedPatient?.partnerID]);

  const receiptPatientAge = useMemo(() => {
    const dob =
      selectedPatient?.patient?.dob ||
      selectedPatient?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.dob ||
      null;
    if (!dob) return "-";
    const age = moment().diff(moment(dob), "years");
    return Number.isFinite(age) && age >= 0 ? `${age} years` : "-";
  }, [
    selectedPatient?.patient?.dob,
    selectedPatient?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.dob,
  ]);

  const receiptPartnerName =
    String(receiptPartner?.basicInfo?.name || "").trim() || "Partner";
  const receiptPartnerAddress =
    receiptPartner?.addressInfo?.fullAddress ||
    [
      receiptPartner?.addressInfo?.address1,
      receiptPartner?.addressInfo?.address2,
      receiptPartner?.addressInfo?.city,
      receiptPartner?.addressInfo?.state,
      receiptPartner?.addressInfo?.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  const receiptPartnerAddressLines = useMemo(
    () => buildReceiptAddressLines(receiptPartnerAddress),
    [receiptPartnerAddress],
  );
  const receiptGSTIN = String(
    receiptPaymentDetails?.gstin ||
      receiptPartner?.verificationDocuments?.GST?.number ||
      "",
  ).trim();
  const receiptPartnerPhones = useMemo(() => {
    const numbers = [
      receiptPartner?.basicInfo?.phone,
      ...(Array.isArray(receiptPartner?.contactInfo)
        ? receiptPartner.contactInfo.map((item) => item?.phone)
        : []),
    ]
      .map((value) => formatReceiptPhoneNumber(value))
      .filter(Boolean);

    return Array.from(new Set(numbers)).join(", ");
  }, [receiptPartner]);
  const receiptPatientName =
    String(selectedPatient?.patient?.name || "").trim() || "-";
  const receiptPatientGender = (() => {
    const gender = String(selectedPatient?.patient?.gender || "")
      .trim()
      .toLowerCase();
    if (gender === "male") return "M";
    if (gender === "female") return "F";
    return gender ? capitalizeFirstLetter(gender) : "-";
  })();
  const receiptDateLabel = receiptPaymentDetails?.paidDateTime
    ? moment(receiptPaymentDetails.paidDateTime).format("DD/MM/YYYY hh:mm A")
    : "-";
  const receiptPaymentMode = receiptPaymentDetails?.paymentMode || "Online";
  const receiptPatientMobile =
    formatPhoneNumber(phone) !== "N/A"
      ? formatPhoneNumber(phone)
      : String(phone || "-");
  const receiptPoweredByLogo = `${window.location.origin}/receipt-logo.png`;
  const currentReceiptError = activeReceiptError || receiptError;
  const receiptReferenceId =
    String(receiptPaymentDetails?.receiptID || "-").trim() || "-";
  const receiptPatientMrn =
    String(
      receiptPaymentDetails?.patientMrn ||
        selectedPatient?.mrn ||
        selectedPatient?.patientInfo?.mrn ||
        selectedPatient?.patient?.mrn ||
        selectedPatient?.patientID?.mrn ||
        "-",
    ).trim() || "-";
  const receiptPreparedBy = (() => {
    const candidate = String(
      selectedPatient?.specialistInfo?.name ||
        selectedPatient?.dutyStaff?.name ||
        receiptPartnerName ||
        "-",
    ).trim();
    if (!candidate || candidate.toLowerCase() === "unassigned")
      return receiptPartnerName || "-";
    return candidate;
  })();

  useEffect(() => {
    if (!receiptSections.length) {
      setActiveReceiptKey("");
      return;
    }

    setActiveReceiptKey((currentKey) => {
      if (receiptSections.some((section) => section.key === currentKey)) {
        return currentKey;
      }
      return receiptSections[0]?.key || "";
    });
  }, [receiptSections]);

  const openReceiptModal = async (targetReceiptKey = "") => {
    if (!selectedPatient?._id) return;
    const availableReceiptSections = receiptSections;
    const initialReceiptKey =
      availableReceiptSections.find(
        (section) => section.key === targetReceiptKey,
      )?.key ||
      availableReceiptSections[0]?.key ||
      "";

    setShowReceiptModal(true);
    setReceiptLoading(true);
    setReceiptError("");
    setReceiptSegmentErrors({});
    setReceiptPaymentDetailsByKey({});
    setActiveReceiptKey(initialReceiptKey);

    try {
const [partnerRes, paymentResults] = await Promise.all([
        getPartner(receiptPartnerId),
        Promise.all(
          availableReceiptSections.map(async (section) => ({
            key: section.key,
            response: await getVisitReceiptDetails(
              selectedPatient._id,
              receiptPartnerId,
              {
                paymentSegment: section.paymentSegment,
                sessionIndex: section.sessionIndex,
                dutyDate: section.dutyDate,
              },
            ),
          })),
        ),
      ]);

      if (partnerRes?.error) {
        throw new Error(
          partnerRes?.message || "Failed to fetch partner details",
        );
      }

      const nextReceiptPaymentDetailsByKey = {};
      const nextReceiptSegmentErrors = {};

      paymentResults.forEach(({ key, response }) => {
        if (response?.error) {
          nextReceiptSegmentErrors[key] =
            response?.message || "Failed to fetch receipt payment details";
          return;
        }

        nextReceiptPaymentDetailsByKey[key] = response?.data || null;
      });

      const firstLoadedReceiptKey =
        availableReceiptSections.find(
          (section) => nextReceiptPaymentDetailsByKey[section.key],
        )?.key || initialReceiptKey;

      if (!Object.keys(nextReceiptPaymentDetailsByKey).length) {
        throw new Error(
          nextReceiptSegmentErrors[initialReceiptKey] ||
            Object.values(nextReceiptSegmentErrors)[0] ||
            "Failed to fetch receipt payment details",
        );
      }

      setReceiptPartner(partnerRes?.data || null);
      setReceiptPaymentDetailsByKey(nextReceiptPaymentDetailsByKey);
      setReceiptSegmentErrors(nextReceiptSegmentErrors);
      setActiveReceiptKey(firstLoadedReceiptKey);
    } catch (error) {
      setReceiptError(error?.message || "Unable to load receipt");
    } finally {
      setReceiptLoading(false);
    }
  };

  const closeReceiptModal = () => {
    setShowReceiptModal(false);
  };

  const handlePrintReceipt = () => {
    const printWindow = window.open("", "_blank", "width=900,height=1200");
    if (!printWindow) return;

    printWindow.document.write(
      buildReceiptPrintMarkup({
        partnerBannerImage: receiptPartner?.basicInfo?.bannerImage,
        partnerBannerImageDataUrl:
          receiptPaymentDetails?.partnerBannerImageDataUrl,
        partnerName: receiptPartnerName,
        partnerAddressLines: receiptPartnerAddressLines,
        partnerPhones: receiptPartnerPhones,
        partnerGST: receiptGSTIN,
        poweredByLogo: receiptPoweredByLogo,
        receiptDateLabel,
        receiptReferenceId,
        patientName: receiptPatientName,
        patientAge: receiptPatientAge,
        patientGender: receiptPatientGender,
        patientMrn: receiptPatientMrn,
        patientMobile: receiptPatientMobile,
        preparedBy: receiptPreparedBy,
        paymentMode: receiptPaymentMode,
        lineItems: receiptLineItems,
        totalAmount: receiptTotalAmount,
        amountInWords: receiptAmountInWords,
      }),
    );
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleDownloadReceipt = async () => {
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    let currentY = 36;
    const left = 34;
    const right = 561;
    const centerX = 297.5;
    const borderColor = [112, 95, 64];

    const logoImage = await loadImageForPdf(
      receiptPaymentDetails?.partnerBannerImageDataUrl ||
        receiptPartner?.basicInfo?.bannerImage,
    );
    if (logoImage?.dataUrl) {
      const logoHeight = 86;
      const logoWidth = Math.min(
        240,
        Math.max(
          120,
          (logoImage.width / Math.max(logoImage.height, 1)) * logoHeight,
        ),
      );
      doc.addImage(
        logoImage.dataUrl,
        logoImage.format,
        centerX - logoWidth / 2,
        currentY,
        logoWidth,
        logoHeight,
      );
      currentY += logoHeight + 8;
    }

    doc.setTextColor(20, 20, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(receiptPartnerName, centerX, currentY, { align: "center" });
    currentY += 18;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    if (receiptPartnerAddressLines.length > 0) {
      doc.text(receiptPartnerAddressLines, centerX, currentY, {
        align: "center",
      });
      currentY += receiptPartnerAddressLines.length * 11;
    }
    if (receiptPartnerPhones) {
      doc.text(`Phone: ${receiptPartnerPhones}`, centerX, currentY, {
        align: "center",
      });
      currentY += 11;
    }
    if (receiptGSTIN) {
      doc.text(`GSTIN: ${receiptGSTIN}`, centerX, currentY, {
        align: "center",
      });
      currentY += 11;
    }

    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.6);
    const receiptLeftRule = 24;
    const receiptRightRule = 571;
    const titleTopRuleY = currentY + 10;
    doc.line(receiptLeftRule, titleTopRuleY, receiptRightRule, titleTopRuleY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const titleTextY = titleTopRuleY + 24;
    doc.text("BILL OF SUPPLY CUM RECEIPT OP", centerX, titleTextY, {
      align: "center",
    });
    const titleBottomRuleY = titleTextY + 18;
    doc.line(
      receiptLeftRule,
      titleBottomRuleY,
      receiptRightRule,
      titleBottomRuleY,
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const topMetaY = titleBottomRuleY + 22;
    doc.text(`Date:`, 380, topMetaY);
    doc.setFont("helvetica", "normal");
    doc.text(receiptDateLabel, 420, topMetaY);

    const infoTop = topMetaY + 16;
    const infoBottom = infoTop + 96;
    const columns = [left, 140, 310, 434, receiptRightRule];
    doc.rect(left, infoTop, receiptRightRule - left, infoBottom - infoTop);
    [columns[1], columns[2], columns[3]].forEach((x) =>
      doc.line(x, infoTop, x, infoBottom),
    );
    doc.line(left, infoTop + 24, receiptRightRule, infoTop + 24);
    doc.line(left, infoTop + 48, receiptRightRule, infoTop + 48);
    doc.line(left, infoTop + 72, receiptRightRule, infoTop + 72);

    const drawInfoLabel = (label, x, y) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, x, y);
    };
    const drawInfoValue = (value, x, y) => {
      doc.setFont("helvetica", "normal");
      doc.text(String(value || "-"), x, y);
    };

    drawInfoLabel("Name:", left + 8, infoTop + 16);
    drawInfoValue(receiptPatientName, 148, infoTop + 16);
    drawInfoLabel("Mobile:", 318, infoTop + 16);
    drawInfoValue(receiptPatientMobile, 442, infoTop + 16);

    drawInfoLabel("Age:", left + 8, infoTop + 40);
    drawInfoValue(receiptPatientAge, 148, infoTop + 40);
    drawInfoLabel("Gender:", 318, infoTop + 40);
    drawInfoValue(receiptPatientGender, 442, infoTop + 40);

    drawInfoLabel("MRN:", left + 8, infoTop + 64);
    drawInfoValue(receiptPatientMrn, 148, infoTop + 64);
    drawInfoLabel("Prepared by:", 318, infoTop + 64);
    drawInfoValue(receiptPreparedBy, 442, infoTop + 64);

    drawInfoLabel("Payment Type:", left + 8, infoTop + 88);
    drawInfoValue(receiptPaymentMode, 148, infoTop + 88);
    drawInfoLabel("Receipt ID:", 318, infoTop + 88);
    drawInfoValue(receiptReferenceId, 442, infoTop + 88);

    const tableTop = infoBottom + 24;
    doc.rect(left, tableTop, receiptRightRule - left, 28);
    const tableColumns = [left, 86, 390, 450, 516, receiptRightRule];
    tableColumns
      .slice(1, -1)
      .forEach((x) => doc.line(x, tableTop, x, tableTop + 28));
    doc.setFont("helvetica", "bold");
    doc.text("S.No", left + 12, tableTop + 18);
    doc.text("Particulars", 96, tableTop + 18);
    doc.text("Qty", 404, tableTop + 18);
    doc.text("Rate", 462, tableTop + 18);
    doc.text("Amount", right, tableTop + 18, { align: "right" });

    doc.setFont("helvetica", "normal");
    currentY = tableTop + 28;
    receiptLineItems.forEach((item, index) => {
      doc.rect(left, currentY, receiptRightRule - left, 28);
      tableColumns
        .slice(1, -1)
        .forEach((x) => doc.line(x, currentY, x, currentY + 28));
      doc.text(`${index + 1}`, left + 16, currentY + 18);
      doc.text(
        String(item.particulars || "-").toUpperCase(),
        96,
        currentY + 18,
      );
      doc.text(String(item.qty || 0), 422, currentY + 18, { align: "right" });
      doc.text(toReceiptAmount(item.rate), 488, currentY + 18, {
        align: "right",
      });
      doc.text(toReceiptAmount(item.amount), right, currentY + 18, {
        align: "right",
      });
      currentY += 28;
    });

    doc.setFont("helvetica", "bold");
    currentY += 28;
    doc.text("Amount (in words):", left, currentY);
    doc.setFont("helvetica", "bold");
    doc.setFont("helvetica", "normal");
    const amountWordsLines = doc.splitTextToSize(receiptAmountInWords, 340);
    doc.text(amountWordsLines, left + 112, currentY);

    currentY += Math.max(24, amountWordsLines.length * 12);
    const summaryTop = currentY + 40;
    const summaryHeight = 38;
    const summaryCols = [left, 299, receiptRightRule];
    doc.rect(left, summaryTop, receiptRightRule - left, summaryHeight);
    summaryCols
      .slice(1, -1)
      .forEach((x) => doc.line(x, summaryTop, x, summaryTop + summaryHeight));
    doc.setFont("helvetica", "bold");
    doc.text("Amount Receivable", left + 16, summaryTop + 23);
    doc.setFont("helvetica", "normal");
    doc.text(`| ${toReceiptAmount(receiptTotalAmount)}`, 206, summaryTop + 23);
    doc.setFont("helvetica", "bold");
    doc.text("Amount Received", 312, summaryTop + 23);
    doc.setFont("helvetica", "normal");
    doc.text(
      `| ${toReceiptAmount(receiptTotalAmount)}`,
      right - 12,
      summaryTop + 23,
      { align: "right" },
    );

    const poweredByLogoImage = await loadImageForPdf(receiptPoweredByLogo);
    doc.setTextColor(29, 90, 97);
    doc.setFont("helvetica", "normal");
    const poweredByTextY = summaryTop + 74;
    if (poweredByLogoImage?.dataUrl) {
      const footerLogoHeight = 26;
      const footerLogoWidth = Math.max(
        56,
        (poweredByLogoImage.width / Math.max(poweredByLogoImage.height, 1)) *
          footerLogoHeight,
      );
      const logoX = right - footerLogoWidth;
      doc.text("Powered by", logoX - 8, poweredByTextY, { align: "right" });
      doc.addImage(
        poweredByLogoImage.dataUrl,
        poweredByLogoImage.format,
        logoX,
        poweredByTextY - footerLogoHeight + 4,
        footerLogoWidth,
        footerLogoHeight,
      );
    } else {
      doc.text("Powered by", right, poweredByTextY, { align: "right" });
    }
    doc.setDrawColor(13, 101, 114);
    doc.setFillColor(13, 101, 114);
    doc.rect(left, summaryTop + 86, receiptRightRule - left, 4, "F");

    doc.save(
      buildReceiptFileName(
        receiptPatientName,
        receiptDateLabel,
        activeReceiptSection?.fileLabel || receiptDocumentLabel,
      ),
    );
  };

  const Card = ({ title, headerAction = null, children, className = "" }) => (
    <div
      className={`flex flex-col rounded-[10px] overflow-hidden border border-[#D7E4EB] shadow-sm bg-white ${className}`}
    >
      <div
        className="px-4 py-2 flex items-center justify-between gap-3 flex-shrink-0"
        style={{ backgroundColor: headerColor }}
      >
        <h3 className="text-[13px] font-medium text-white break-words">
          {title}
        </h3>
        {headerAction}
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );

  return (
    <div className=" min-[1100px]:h-full h-auto w-full flex-shrink-0 font-poppins">
      {!hasAnyData ? (
        <div className={`${panelHeightClass}`}>
          <Card title="Patient Information" className="h-full">
            <div className="h-full flex items-center justify-center">
              <div className="text-[12px] text-[#6b6b6b] text-center">
                No Information to display
              </div>
            </div>
          </Card>
        </div>
      ) : (
        <div
          className={`sticky top-2 ${panelHeightClass} flex flex-col min-h-0 w-full`}
        >
          {/* <div className="mb-2">
            <p className=" text-start text-[14px] font-medium underline">
              Booking Information ({jobID})
            </p>
          </div> */}
          {/* ✅ ONE scroll container for the whole right panel */}
          <div className="flex-1 min-h-0 min-[1100px]:overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 pr-1 space-y-4">
            {showPatientListDetails && (
              <Card title="Patient Information">
                <div className="space-y-3">
                  <div className="flex text-[12px] gap-1">
                    <span className="text-[#2d2d2d] font-semibold w-[40%]">
                      MRN
                    </span>
                    <span className="text-[#464646] mx-1">:</span>
                    <span className="text-[#6b6b6b] flex-1 break-words">
                      {patientListMrn}
                    </span>
                  </div>
                  <div className="flex text-[12px] gap-1">
                    <span className="text-[#2d2d2d] font-semibold w-[40%]">
                      Last Prescribed
                    </span>
                    <span className="text-[#464646] mx-1">:</span>
                    <span className="text-[#6b6b6b] flex-1 break-words">
                      {patientListLastPrescribed}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {!hideBookingInfo && (
              <Card title="Booking Information">
                <div className="space-y-3 px-1 py-1">
                  <div className="flex text-[12px] gap-1 items-start">
                    <span className="text-[#2d2d2d] font-semibold w-[45%] shrink-0">
                      Booking ID
                    </span>
                    <span className="text-[#464646] mx-1">:</span>
                    <span className="text-[#6b6b6b] flex-1 break-words flex items-center gap-2">
                      {jobID || "-"}
                    </span>
                  </div>
                  {showDutyType && (
                    <div className="flex text-[12px] gap-1 items-start">
                      <span className="text-[#2d2d2d] font-semibold w-[45%] shrink-0">
                        Duty Type
                      </span>
                      <span className="text-[#464646] mx-1">:</span>
                      <span className="text-[#6b6b6b] flex-1 break-words">
                        {getDutyTypeDisplay(selectedPatient)}
                      </span>
                    </div>
                  )}
                  <div className="flex text-[12px] gap-1 items-start">
                    <span className="text-[#2d2d2d] font-semibold w-[45%] shrink-0">
                      Total Days
                    </span>
                    <span className="text-[#464646] mx-1">:</span>
                    <span className="text-[#6b6b6b] flex-1 break-words">
                      {totalDutyDays || "-"}
                    </span>
                  </div>
                  <div className="flex text-[12px] gap-1 items-start">
                    <span className="text-[#2d2d2d] font-semibold w-[45%] shrink-0 leading-tight pr-1">
                      Requested Services/Complaints
                    </span>
                    <span className="text-[#464646] mx-1">:</span>
                    <span className="text-[#6b6b6b] flex-1 break-words leading-tight">
                      {services.length > 0 ? (
                        services.map((service, index) => (
                          <span key={`${service}-${index}`}>
                            {service}
                            {index < services.length - 1 && ", "}
                          </span>
                        ))
                      ) : (
                        <span className="italic">No services listed</span>
                      )}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            <Card title="Contact Information">
              <div className="space-y-3">
                <div className="flex text-[12px] gap-1">
                  <span className="text-[#2d2d2d] font-semibold w-[40%]">
                    Phone Number
                  </span>
                  <span className="text-[#464646] mx-1">:</span>
                  <span className="text-[#6b6b6b] flex-1 flex items-center gap-1 break-all">
                    <MdCall color={headerColor} size={14} />
                    {formatPhoneNumber(phone) || "-"}
                  </span>
                </div>

                <div className="flex text-[12px] gap-1 items-start">
                  <span className="text-[#2d2d2d] font-semibold w-[40%]">
                    Address
                  </span>
                  <span className="text-[#464646] mx-1 mt-[1px]">:</span>
                  <span className="text-[#6b6b6b] flex-1 break-words">
                    {fullAddress ? (
                      <button
                        onClick={() => setShowMap(true)}
                        className="underline text-[#2d2d2d] hover:text-[#4E7DA6] text-left transition-colors break-words"
                      >
                        {fullAddress}
                      </button>
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
              </div>
            </Card>

            <Card title="Discharge Summaries">
              <div className="space-y-2">
                {dischargeSummariesLoading ? (
                  <div className="text-[12px] text-[#6b6b6b] italic">
                    Loading discharge summaries...
                  </div>
                ) : dischargeSummariesError ? (
                  <div className="text-[12px] text-red-500">
                    {dischargeSummariesError}
                  </div>
                ) : Array.isArray(dischargeSummaries) &&
                  dischargeSummaries.length > 0 ? (
                  dischargeSummaries.map((item, index) => (
                    <div
                      key={item?._id || `${item?.title || "summary"}-${index}`}
                      className="border border-gray-100 rounded-lg px-2 py-1.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[12px] text-[#2d2d2d] font-semibold break-words">
                            {item?.title || `Discharge Summary ${index + 1}`}
                          </div>
                          <div className="text-[10px] text-[#6b6b6b] mt-0.5">
                            {item?.date
                              ? moment(item.date).format("DD MMM YYYY, hh:mm A")
                              : "-"}
                          </div>
                        </div>

                        {item?.key ? (
                          <a
                            href={item.key}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-[10px] px-2 py-1 rounded bg-blue-50 text-[#3E57E8] hover:bg-blue-100 transition-colors"
                          >
                            View
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-[12px] text-[#6b6b6b] italic">
                    No discharge summaries uploaded
                  </div>
                )}
              </div>
            </Card>

            {(isNonAssessmentPhysioVisit || isNurseOneTimeVisit) && (
              <Card
                title="Order Amount"
                headerAction={
                  showReceiptAction ? (
                    <button
                      onClick={openReceiptModal}
                      className="text-[11px] font-semibold text-white underline underline-offset-2 hover:opacity-80"
                    >
                      Receipt
                    </button>
                  ) : null
                }
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                    <span className="text-[#2d2d2d]">Payment Type</span>
                    <span className="text-[#2d2d2d] font-semibold break-words text-right">
                      {orderPaymentType}
                    </span>
                  </div>

                  {orderServiceFees.length > 0 ? (
                    <>
                      {orderServiceFees.map((item, index) => {
                        const serviceName = String(
                          item?.service || item?.name || `Service ${index + 1}`,
                        ).trim();
                        const amount = Number(item?.fee ?? item?.fees ?? 0);
                        return (
                          <div
                            key={`${serviceName}-${index}`}
                            className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5 last:border-0 last:pb-0"
                          >
                            <span className="text-[#2d2d2d] truncate">
                              {serviceName || "-"}
                            </span>
                            <span className="text-[#6b6b6b] break-words">
                              Rs.{" "}
                              {Number.isFinite(amount)
                                ? amount.toFixed(2)
                                : "0.00"}
                            </span>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between gap-2 text-[12px] border-t border-gray-200 pt-1.5">
                        <span className="text-[#2d2d2d]">Total Days</span>
                        <span className="text-[#6b6b6b] break-words">
                          {Number.isFinite(orderAmountTotalDays)
                            ? orderAmountTotalDays
                            : 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-200 text-[12px] font-semibold">
                        <span className="text-[#2d2d2d]">Total</span>
                        <span className="text-[#2d2d2d]">
                          Rs. {orderAmountTotal.toFixed(2)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-[12px] text-[#6b6b6b] italic">
                      No service fees available
                    </div>
                  )}
                </div>
              </Card>
            )}

            {isAssessmentPhysioVisit && (
              <Card
                title="Order Amount"
                headerAction={
                  showReceiptAction ? (
                    <button
                      onClick={openReceiptModal}
                      className="text-[11px] font-semibold text-white underline underline-offset-2 hover:opacity-80"
                    >
                      Receipt
                    </button>
                  ) : null
                }
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                    <span className="text-[#2d2d2d]">Payment Type</span>
                    <span className="text-[#2d2d2d] font-semibold break-words text-right">
                      {orderPaymentType}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                    <span className="text-[#2d2d2d]">Assessment Fee</span>
                    <span className="text-[#6b6b6b] break-words">
                      Rs.{" "}
                      {Number.isFinite(physioAssessmentFee)
                        ? physioAssessmentFee.toFixed(2)
                        : "0.00"}
                    </span>
                  </div>

                  {physioSessions.map((session, index) => {
                    const extendedDays = Number(session?.extendedDays ?? 0);
                    const feesPerSession = Number(session?.feesPerSession ?? 0);
                    const extensionAmount = Number(
                      session?.extensionFees ?? session?.grandTotal ?? 0,
                    );
                    const sessionLabel = getPhysioSessionLabel(
                      selectedPatient,
                      session,
                      index,
                    );

                    return (
                      <div
                        key={`physio-session-${index}`}
                        className="flex items-start justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5 last:border-0 last:pb-0"
                      >
                        <span className="text-[#2d2d2d]">
                          {sessionLabel}
                          {Number.isFinite(extendedDays) && extendedDays > 0
                            ? ` (${extendedDays} days)`
                            : ""}
                          {Number.isFinite(feesPerSession) && feesPerSession > 0
                            ? ` @ Rs. ${feesPerSession.toFixed(2)}`
                            : ""}
                        </span>
                        <span className="text-[#6b6b6b] break-words">
                          Rs.{" "}
                          {Number.isFinite(extensionAmount)
                            ? extensionAmount.toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between gap-2 text-[12px] border-t border-gray-200 pt-1.5">
                    <span className="text-[#2d2d2d]">Total Days</span>
                    <span className="text-[#6b6b6b] break-words">
                      {Number.isFinite(orderAmountTotalDays)
                        ? orderAmountTotalDays
                        : 0}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-200 text-[12px] font-semibold">
                    <span className="text-[#2d2d2d]">Total</span>
                    <span className="text-[#2d2d2d] break-words">
                      Rs.{" "}
                      {Number.isFinite(physioAssessmentTotal)
                        ? physioAssessmentTotal.toFixed(2)
                        : "0.00"}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {isNurseRegularVisit && (
              <Card
                title="Order Amount"
                headerAction={
                  showReceiptAction ? (
                    <button
                      onClick={openReceiptModal}
                      className="text-[11px] font-semibold text-white underline underline-offset-2 hover:opacity-80"
                    >
                      Receipt
                    </button>
                  ) : null
                }
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                    <span className="text-[#2d2d2d]">Payment Type</span>
                    <span className="text-[#2d2d2d] font-semibold break-words text-right">
                      {orderPaymentType}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                    <span className="text-[#2d2d2d]">Fee Per Day</span>
                    <span className="text-[#6b6b6b] break-words">
                      Rs.{" "}
                      {Number.isFinite(nurseFeePerDay)
                        ? nurseFeePerDay.toFixed(2)
                        : "0.00"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[12px] border-b border-gray-100 pb-1.5">
                    <span className="text-[#2d2d2d]">Total Days</span>
                    <span className="text-[#6b6b6b] break-words">
                      {Number.isFinite(nurseTotalDays) ? nurseTotalDays : 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[12px] font-semibold">
                    <span className="text-[#2d2d2d]">Total Fees</span>
                    <span className="text-[#2d2d2d] break-words">
                      Rs.{" "}
                      {Number.isFinite(nurseTotalFees)
                        ? nurseTotalFees.toFixed(2)
                        : "0.00"}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* {completedAssessments && completedAssessments.length > 0 && (
                            completedAssessments.map((assess, idx) => {
                                const isOpen = !!openAssessments[idx];
                                const reportTitle = completedAssessments.length > 1
                                    ? `Physiotherapy Assessment Report ${idx + 1}`
                                    : "Physiotherapy Assessment Report";

                                return (
                                    <Card
                                        key={assess?._id || idx}
                                        title={reportTitle}
                                        headerAction={
                                            <button
                                                type="button"
                                                onClick={() => setOpenAssessments(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                                className="text-[11px] font-semibold text-white underline underline-offset-2 hover:opacity-80 font-poppins"
                                            >
                                                {isOpen ? "Hide" : "Show"}
                                            </button>
                                        }
                                        className="mb-4"
                                    >
                                        {isOpen ? (
                                            <div className="space-y-3 text-[12px]">
                                                <div className="flex gap-1">
                                                    <span className="text-[#2d2d2d] font-semibold w-[40%]">Assessor</span>
                                                    <span className="text-[#464646] mx-1">:</span>
                                                    <span className="text-[#6b6b6b] flex-1 break-words">
                                                        {selectedPatient?.assessmentStaff?.name || assess?.assessmentStaff?.name || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1">
                                                    <span className="text-[#2d2d2d] font-semibold w-[40%]">Complaint</span>
                                                    <span className="text-[#464646] mx-1">:</span>
                                                    <span className="text-[#6b6b6b] flex-1 break-words">
                                                        {assess?.complaints?.complaint || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1">
                                                    <span className="text-[#2d2d2d] font-semibold w-[40%]">Location</span>
                                                    <span className="text-[#464646] mx-1">:</span>
                                                    <span className="text-[#6b6b6b] flex-1 break-words">
                                                        {assess?.complaints?.location || "-"}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1">
                                                    <span className="text-[#2d2d2d] font-semibold w-[40%]">Duration</span>
                                                    <span className="text-[#464646] mx-1">:</span>
                                                    <span className="text-[#6b6b6b] flex-1 break-words">
                                                        {assess?.complaints?.duration || "-"}
                                                    </span>
                                                </div>
                                                {assess?.treatmentPlan?.targetArea && (
                                                    <div className="flex gap-1">
                                                        <span className="text-[#2d2d2d] font-semibold w-[40%]">Target Area</span>
                                                        <span className="text-[#464646] mx-1">:</span>
                                                        <span className="text-[#6b6b6b] flex-1 break-words">
                                                            {assess.treatmentPlan.targetArea}
                                                        </span>
                                                    </div>
                                                )}
                                                {assess?.treatmentPlan?.sessionFrequency && (
                                                    <div className="flex gap-1">
                                                        <span className="text-[#2d2d2d] font-semibold w-[40%]">Frequency</span>
                                                        <span className="text-[#464646] mx-1">:</span>
                                                        <span className="text-[#6b6b6b] flex-1 break-words">
                                                            {assess.treatmentPlan.sessionFrequency}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-1">
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenAssessments(prev => ({ ...prev, [idx]: true }))}
                                                    className="text-[12px] text-[#3E57E8] font-medium hover:underline font-poppins"
                                                >
                                                    Click to view assessment details
                                                </button>
                                            </div>
                                        )}
                                    </Card>
                                );
                            })
                        )} */}

            <Card title="Patient History">
              {hasPatientHistoryData ? (
                <div className="space-y-6">
                  {preExistingConditionsData.length > 0 && (
                    <div>
                      <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                        Pre-Existing Condition
                      </h5>
                      {preExistingConditionsData.map((condition, index) => (
                        <div
                          key={`pre-${index}`}
                          className="space-y-1.5 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                        >
                          <div className="flex text-[12px] gap-1">
                            <span className="text-[#2d2d2d] font-semibold w-[40%]">
                              Condition
                            </span>
                            <span className="text-[#464646] mx-1">:</span>
                            <span className="text-[#6b6b6b] flex-1 break-words">
                              {condition.condition || "-"}
                            </span>
                          </div>
                          <div className="flex text-[12px] gap-1">
                            <span className="text-[#2d2d2d] font-semibold w-[40%]">
                              Since
                            </span>
                            <span className="text-[#464646] mx-1">:</span>
                            <span className="text-[#6b6b6b] flex-1 break-words">
                              {condition.diagnosedDate
                                ? moment(condition.diagnosedDate).format(
                                    "MMM YYYY",
                                  )
                                : condition.sinceWhen || "-"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {allergiesData.length > 0 && (
                    <div>
                      <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                        Allergies
                      </h5>
                      {allergiesData.map((a, i) => (
                        <div
                          key={`alg-${i}`}
                          className="space-y-1.5 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                        >
                          <div className="flex text-[12px] gap-1">
                            <span className="text-[#2d2d2d] font-semibold w-[40%]">
                              Type
                            </span>
                            <span className="text-[#464646] mx-1">:</span>
                            <span className="text-[#6b6b6b] flex-1 break-words">
                              {a.allergen || "-"}
                            </span>
                          </div>
                          {a.allergenReason && (
                            <div className="flex text-[12px] gap-1">
                              <span className="text-[#2d2d2d] font-semibold w-[40%]">
                                Reason
                              </span>
                              <span className="text-[#464646] mx-1">:</span>
                              <span className="text-[#6b6b6b] flex-1 break-words">
                                {a.allergenReason}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {medicalHistoryData.length > 0 && (
                    <div>
                      <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                        Medical History
                      </h5>
                      {medicalHistoryData.map((m, i) => (
                        <div
                          key={`mh-${i}`}
                          className="flex text-[12px] gap-1 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                        >
                          <span className="text-[#2d2d2d] font-semibold w-[40%]">
                            Diagnosis
                          </span>
                          <span className="text-[#464646] mx-1">:</span>
                          <span className="text-[#6b6b6b] flex-1 break-words">
                            {m.diagnosis || "-"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {familyHistoryData.length > 0 && (
                    <div>
                      <h5 className="text-[12px] font-bold text-[#4E7DA6] mb-2 uppercase tracking-tight">
                        Family Medical History
                      </h5>
                      {familyHistoryData.map((f, i) => (
                        <div
                          key={`fh-${i}`}
                          className="space-y-1.5 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                        >
                          <div className="flex text-[12px] gap-1">
                            <span className="text-[#2d2d2d] font-semibold w-[40%]">
                              Relation
                            </span>
                            <span className="text-[#464646] mx-1">:</span>
                            <span className="text-[#6b6b6b] flex-1 break-words">
                              {capitalizeFirstLetter(
                                f.relationToPatient || "-",
                              )}
                            </span>
                          </div>

                          {f.medicalCondition && (
                            <div className="flex text-[12px] gap-1">
                              <span className="text-[#2d2d2d] font-semibold w-[40%]">
                                Condition
                              </span>
                              <span className="text-[#464646] mx-1">:</span>
                              <span className="text-[#6b6b6b] flex-1 break-words">
                                {f.medicalCondition}
                              </span>
                            </div>
                          )}

                          {f.diagnosedAge && (
                            <div className="flex text-[12px] gap-1">
                              <span className="text-[#2d2d2d] font-semibold w-[40%]">
                                Age
                              </span>
                              <span className="text-[#464646] mx-1">:</span>
                              <span className="text-[#6b6b6b] flex-1 break-words">
                                {f.diagnosedAge}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[12px] text-[#6b6b6b] italic text-center py-2">
                  No Information to display
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {showReceiptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-3 py-6 font-poppins">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
              <div>
                <div className="text-[16px] font-semibold text-[#2d2d2d]">
                  {receiptDocumentLabel}
                </div>
                <div className="text-[11px] text-[#6b6b6b]">
                  Generated from visit and payment data
                </div>
                {receiptSections.length > 1 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {receiptSections.map((section) => {
                      const isActive =
                        section.key === activeReceiptSection?.key;
                      return (
                        <button
                          key={section.key}
                          type="button"
                          onClick={() => setActiveReceiptKey(section.key)}
                          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                            isActive
                              ? "border-[#546bb0] bg-[#546bb0] text-white"
                              : "border-gray-300 text-[#2d2d2d] hover:bg-gray-50"
                          }`}
                        >
                          {section.selectorLabel}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadReceipt}
                  disabled={
                    receiptLoading ||
                    Boolean(currentReceiptError) ||
                    !receiptPaymentDetails
                  }
                  className="px-3 py-1.5 text-[12px] rounded-md border border-gray-300 text-[#2d2d2d] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Download
                </button>
                <button
                  onClick={handlePrintReceipt}
                  disabled={
                    receiptLoading ||
                    Boolean(currentReceiptError) ||
                    !receiptPaymentDetails
                  }
                  className="px-3 py-1.5 text-[12px] rounded-md border border-gray-300 text-[#2d2d2d] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Print
                </button>
                <button
                  onClick={closeReceiptModal}
                  className="h-9 w-9 rounded-full border border-gray-300 text-[#2d2d2d] hover:bg-gray-50"
                >
                  x
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-[#f5f6f8] p-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
              {receiptLoading ? (
                <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-[13px] text-[#6b6b6b]">
                  Loading receipt...
                </div>
              ) : currentReceiptError ? (
                <div className="bg-white rounded-lg border border-red-200 p-10 text-center text-[13px] text-red-500">
                  {currentReceiptError}
                </div>
              ) : (
                <div
                  id="partner-portal-receipt"
                  className="relative overflow-hidden max-w-5xl mx-auto shadow-[0_28px_60px_rgba(51,35,14,0.18)]"
                  style={{
                    fontFamily: "Arial, Helvetica, sans-serif",
                    background:
                      "radial-gradient(circle at top, rgba(255,255,255,0.96), rgba(247,242,232,0.98)), linear-gradient(180deg, #f8f2e5 0%, #fbf8f1 100%)",
                  }}
                >
                  {receiptPaymentDetails?.partnerBannerImageDataUrl ||
                  receiptPartner?.basicInfo?.bannerImage ? (
                    <div
                      className="pointer-events-none absolute inset-0 opacity-[0.06] bg-center bg-no-repeat bg-[length:340px_auto]"
                      style={{
                        backgroundImage: `url(${receiptPaymentDetails?.partnerBannerImageDataUrl || receiptPartner?.basicInfo?.bannerImage})`,
                        backgroundPosition: "center 58%",
                      }}
                    />
                  ) : null}

                  <div className="relative px-8 py-9 sm:px-12 sm:py-10">
                    <div className="text-center">
                      {receiptPartner?.basicInfo?.bannerImage ? (
                        <img
                          src={receiptPartner.basicInfo.bannerImage}
                          alt={receiptPartnerName}
                          className="w-[240px] max-w-[42%] h-auto object-contain mx-auto mb-3"
                        />
                      ) : null}
                      <div className="text-[24px] sm:text-[26px] font-bold text-black leading-none">
                        {receiptPartnerName}
                      </div>
                      {receiptPartnerAddressLines.length > 0 ? (
                        <div className="mt-2">
                          {receiptPartnerAddressLines.map((line, index) => (
                            <div
                              key={`${line}-${index}`}
                              className="text-[12px] text-black leading-[1.2]"
                            >
                              {line}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {receiptPartnerPhones ? (
                        <div className="text-[12px] text-black leading-[1.2]">
                          Phone: {receiptPartnerPhones}
                        </div>
                      ) : null}
                      {receiptGSTIN ? (
                        <div className="text-[12px] text-black leading-[1.2]">
                          GSTIN: {receiptGSTIN}
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-3 border-t border-[rgba(74,58,37,0.35)]" />
                    <div className="py-3 text-center">
                      <div className="text-[13px] font-bold text-black tracking-[0.01em]">
                        BILL OF SUPPLY CUM RECEIPT OP
                      </div>
                    </div>
                    <div className="border-t border-[rgba(74,58,37,0.35)]" />

                    <div className="flex justify-end gap-3 pt-4 text-[12px] font-bold text-black">
                      <div>
                        Date:{" "}
                        <span className="font-normal">{receiptDateLabel}</span>
                      </div>
                    </div>

                    <div className="mt-4 border border-[rgba(74,58,37,0.45)] text-[12px] text-black">
                      <div className="grid grid-cols-[106px_minmax(0,1fr)_106px_180px]">
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          Name:
                        </div>
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2">
                          {receiptPatientName}
                        </div>
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          Mobile:
                        </div>
                        <div className="border-b border-[rgba(74,58,37,0.45)] px-3 py-2">
                          {receiptPatientMobile}
                        </div>
                      </div>
                      <div className="grid grid-cols-[106px_minmax(0,1fr)_106px_180px]">
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          Age:
                        </div>
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2">
                          {receiptPatientAge}
                        </div>
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          Gender:
                        </div>
                        <div className="border-b border-[rgba(74,58,37,0.45)] px-3 py-2">
                          {receiptPatientGender}
                        </div>
                      </div>
                      <div className="grid grid-cols-[106px_minmax(0,1fr)_160px_minmax(0,1fr)]">
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          MRN:
                        </div>
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2">
                          {receiptPatientMrn}
                        </div>
                        <div className="border-r border-b border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          Prepared by:
                        </div>
                        <div className="border-b border-[rgba(74,58,37,0.45)] px-3 py-2">
                          {receiptPreparedBy}
                        </div>
                      </div>
                      <div className="grid grid-cols-[160px_minmax(0,1fr)_140px_140px]">
                        <div className="border-r border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          Payment Type:
                        </div>
                        <div className="border-r border-[rgba(74,58,37,0.45)] px-3 py-2">
                          {receiptPaymentMode}
                        </div>
                        <div className="border-r border-[rgba(74,58,37,0.45)] px-3 py-2 font-bold">
                          Receipt ID:
                        </div>
                        <div className="px-3 py-2">{receiptReferenceId}</div>
                      </div>
                    </div>

                    <div className="mt-6 overflow-x-auto text-[12px] text-black scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="text-black">
                            <th className="px-3 py-3 text-left font-bold border border-[rgba(74,58,37,0.45)]">
                              S.No
                            </th>
                            <th className="px-3 py-3 text-left font-bold border border-[rgba(74,58,37,0.45)]">
                              Particulars
                            </th>
                            <th className="px-3 py-3 text-right font-bold border border-[rgba(74,58,37,0.45)]">
                              Qty
                            </th>
                            <th className="px-3 py-3 text-right font-bold border border-[rgba(74,58,37,0.45)]">
                              Rate
                            </th>
                            <th className="px-3 py-3 text-right font-bold border border-[rgba(74,58,37,0.45)]">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {receiptLineItems.map((item, index) => (
                            <tr key={`${item.particulars}-${index}`}>
                              <td className="px-3 py-3 align-top border border-[rgba(74,58,37,0.45)]">
                                {index + 1}
                              </td>
                              <td className="px-3 py-3 align-top border border-[rgba(74,58,37,0.45)] uppercase tracking-[0.01em]">
                                {item.particulars}
                              </td>
                              <td className="px-3 py-3 text-right align-top border border-[rgba(74,58,37,0.45)]">
                                {item.qty}
                              </td>
                              <td className="px-3 py-3 text-right align-top border border-[rgba(74,58,37,0.45)]">
                                {toReceiptAmount(item.rate)}
                              </td>
                              <td className="px-3 py-3 text-right align-top border border-[rgba(74,58,37,0.45)]">
                                {toReceiptAmount(item.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-6 text-[12px] text-black">
                      <div>
                        <span className="font-bold">Amount (in words):</span>{" "}
                        {receiptAmountInWords}
                      </div>
                    </div>

                    <div className="mt-12 border-y border-[rgba(74,58,37,0.45)] text-[12px] text-black">
                      <div className="grid grid-cols-2">
                        <div className="px-3 py-4 border-r border-[rgba(74,58,37,0.45)]">
                          <span className="font-bold">Amount Receivable</span>{" "}
                          <span className="font-normal">
                            | {toReceiptAmount(receiptTotalAmount)}
                          </span>
                        </div>
                        <div className="px-3 py-4">
                          <span className="font-bold">Amount Received</span>{" "}
                          <span className="font-normal">
                            | {toReceiptAmount(receiptTotalAmount)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 flex items-center justify-end gap-2 text-right text-[12px] text-[#1d5a61]">
                      <span>Powered by</span>
                      <img
                        src={receiptPoweredByLogo}
                        alt="Ceviliyar"
                        className="h-6 w-auto object-contain"
                      />
                    </div>
                  </div>
                  <div className="h-[10px] bg-[linear-gradient(90deg,#0d6572,#c89b40)]" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Google Map Modal */}
      {showMap && fullAddress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-poppins">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-hidden">
            <div
              className="flex justify-between items-center p-4"
              style={{ backgroundColor: headerColor }}
            >
              <h3 className="text-[16px] font-semibold text-white">
                Patient Address Location
              </h3>
              <button
                onClick={() => setShowMap(false)}
                className="text-white text-xl hover:opacity-80"
              >
                Ã—
              </button>
            </div>

            <div className="p-4">
              <div className="text-[13px] text-[#2d2d2d] mb-3">
                {fullAddress}
              </div>

              {lat && lng ? (
                <iframe
                  title="Patient Location"
                  src={`https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`}
                  width="100%"
                  height="400"
                  className="rounded-lg border border-gray-200"
                  loading="lazy"
                  allowFullScreen
                />
              ) : (
                <div className="text-sm text-red-500">
                  Location coordinates not available.
                </div>
              )}

              {googleMapUrl && (
                <div className="mt-4 text-right">
                  <a
                    href={googleMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#4E7DA6] text-white text-sm px-4 py-2 rounded-md hover:bg-[#3b6a8a] transition"
                  >
                    Open in Google Maps
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const toReceiptAmount = (amount) => Number(amount || 0).toFixed(2);

const formatReceiptPhoneNumber = (phoneNumber) => {
  const formatted = formatPhoneNumber(phoneNumber);
  if (!formatted || formatted === "N/A") return "";

  const match = formatted.match(/^(\+\d+)\s(\d{10})$/);
  if (!match) return formatted;

  const [, countryCode, localNumber] = match;
  if (countryCode === "+91") {
    return `${countryCode} ${localNumber.slice(0, 5)} ${localNumber.slice(5)}`;
  }

  return formatted;
};

const buildReceiptFileName = (
  patientName,
  receiptDateLabel,
  receiptLabel = "receipt",
) => {
  const safeName = String(patientName || "receipt")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  const safeLabel = String(receiptLabel || "receipt")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  const safeDate = String(receiptDateLabel || "")
    .replace(/[^\d]/g, "")
    .trim();
  return `${safeName || "receipt"}-${safeLabel || "receipt"}-${safeDate || "receipt"}.pdf`;
};

const buildReceiptAddressLines = (address) => {
  const parts = String(address || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return [];

  const lines = [];
  let currentLine = "";
  const maxLineLength = 48;

  parts.forEach((part) => {
    const candidate = currentLine ? `${currentLine}, ${part}` : part;
    if (!currentLine || candidate.length <= maxLineLength) {
      currentLine = candidate;
      return;
    }
    lines.push(currentLine);
    currentLine = part;
  });

  if (currentLine) lines.push(currentLine);
  if (lines.length <= 3) return lines;

  return [lines[0], lines[1], lines.slice(2).join(", ")];
};

const loadImageForPdf = async (src) => {
  const imageSrc = String(src || "").trim();
  if (!imageSrc) return null;

  const dataUrl = /^data:image\//i.test(imageSrc)
    ? imageSrc
    : await fetchImageAsDataUrl(imageSrc);

  if (!dataUrl) return null;

  const format = dataUrl.toLowerCase().includes("image/png") ? "PNG" : "JPEG";
  const dimensions = await getImageDimensions(dataUrl);
  return {
    dataUrl,
    format,
    width: dimensions?.width || 100,
    height: dimensions?.height || 50,
  };
};

const fetchImageAsDataUrl = async (src) => {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};

const getImageDimensions = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = src;
  });

const buildReceiptPrintMarkup = ({
  partnerBannerImage,
  partnerBannerImageDataUrl,
  partnerName,
  partnerAddressLines,
  partnerPhones,
  partnerGST,
  poweredByLogo,
  receiptDateLabel,
  receiptReferenceId,
  patientName,
  patientAge,
  patientGender,
  patientMrn,
  patientMobile,
  preparedBy,
  paymentMode,
  lineItems,
  totalAmount,
  amountInWords,
}) => {
  const lineRows = (Array.isArray(lineItems) ? lineItems : [])
    .map(
      (item, index) => `
                <tr>
                    <td>${index + 1}.</td>
                    <td>${item.particulars || "-"}</td>
                    <td class="num">${item.qty || 0}</td>
                    <td class="num">${toReceiptAmount(item.rate)}</td>
                    <td class="num">${toReceiptAmount(item.amount)}</td>
                </tr>
            `,
    )
    .join("");

  const logoSource = partnerBannerImageDataUrl || partnerBannerImage || "";
  const addressMarkup = Array.isArray(partnerAddressLines)
    ? partnerAddressLines
        .filter(Boolean)
        .map((line) => `<p class="header-line">${line}</p>`)
        .join("")
    : "";

  return `
        <!DOCTYPE html>
        <html>
            <head>
                <meta charset="utf-8" />
                <title>Receipt</title>
                <style>
                    body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 24px; background: #ece6db; color: #141414; }
                    .receipt {
                        position: relative;
                        max-width: 980px;
                        margin: 0 auto;
                        overflow: hidden;
                        background:
                            radial-gradient(circle at top, rgba(255,255,255,0.96), rgba(247,242,232,0.98)),
                            linear-gradient(180deg, #f8f2e5 0%, #fbf8f1 100%);
                        box-shadow: 0 24px 60px rgba(51, 35, 14, 0.18);
                    }
                    .receipt:after {
                        content: "";
                        position: absolute;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        height: 10px;
                        background: linear-gradient(90deg, #0d6572, #c89b40);
                    }
                    .watermark {
                        position: absolute;
                        inset: 0;
                        background-image: ${logoSource ? `url('${logoSource}')` : "none"};
                        background-repeat: no-repeat;
                        background-position: center 58%;
                        background-size: 320px auto;
                        opacity: 0.06;
                        pointer-events: none;
                    }
                    .body { position: relative; padding: 38px 46px 46px; }
                    .brand { text-align: center; }
                    .logo { display:block; width: 250px; max-width: 42%; height: auto; margin: 0 auto 10px; object-fit: contain; }
                    .title { font-size: 25px; font-weight: 700; line-height: 1.1; margin: 0; }
                    .header-line { font-size: 12px; line-height: 1.25; margin: 0; }
                    .rule { margin: 12px 0 10px; border-top: 1px solid rgba(74, 58, 37, 0.35); }
                    .receipt-title { text-align: center; font-size: 12px; font-weight: 700; letter-spacing: 0.01em; margin: 12px 0 10px; }
                    .top-meta { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 16px; font-size: 11px; font-weight: 700; }
                    .top-meta span { font-weight: 400; }
                    .info-table, .line-table, .summary-table { width: 100%; border-collapse: collapse; font-size: 11px; }
                    .info-table td, .line-table th, .line-table td, .summary-table td { border: 1px solid rgba(74, 58, 37, 0.45); padding: 8px 10px; vertical-align: middle; }
                    .label { font-weight: 700; white-space: nowrap; }
                    .line-table { margin-top: 16px; }
                    .line-table th { text-align: left; font-size: 11px; font-weight: 700; }
                    .num { text-align: right; }
                    .amount-words { margin: 22px 0 8px; font-size: 11px; }
                    .token { font-size: 11px; font-weight: 700; margin-bottom: 56px; }
                    .summary-table { margin-top: 18px; }
                    .summary-table .label { font-size: 11px; }
                    .summary-value { white-space: nowrap; text-align: right; }
                    .powered {
                        margin-top: 18px;
                        font-size: 11px;
                        color: #1d5a61;
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        gap: 8px;
                    }
                    .powered-logo {
                        height: 22px;
                        width: auto;
                        object-fit: contain;
                    }
                    @media print {
                        body { padding: 0; background: #fff; }
                        .receipt { box-shadow: none; }
                    }
                </style>
            </head>
            <body>
                <div class="receipt">
                    <div class="watermark"></div>
                    <div class="body">
                        <div class="brand">
                            ${logoSource ? `<img class="logo" src="${logoSource}" alt="${partnerName}" />` : ``}
                            <div class="title">${partnerName || "Partner"}</div>
                            ${addressMarkup}
                            ${partnerPhones ? `<p class="header-line">Phone: ${partnerPhones}</p>` : ``}
                            ${partnerGST ? `<p class="header-line">GSTIN: ${partnerGST}</p>` : ``}
                        </div>
                        <div class="rule"></div>
                        <div class="receipt-title">BILL OF SUPPLY CUM RECEIPT OP</div>
                        <div class="rule"></div>

                        <div class="top-meta" style="justify-content:flex-end">
                            <div>Date: <span>${receiptDateLabel || "-"}</span></div>
                        </div>

                        <table class="info-table">
                            <tbody>
                                <tr>
                                    <td class="label">Name:</td>
                                    <td>${patientName || "-"}</td>
                                    <td class="label">Mobile:</td>
                                    <td>${patientMobile || "-"}</td>
                                </tr>
                                <tr>
                                    <td class="label">Age:</td>
                                    <td>${patientAge || "-"}</td>
                                    <td class="label">Gender:</td>
                                    <td>${patientGender || "-"}</td>
                                </tr>
                                <tr>
                                    <td class="label">MRN:</td>
                                    <td>${patientMrn || "-"}</td>
                                    <td class="label">Prepared by:</td>
                                    <td>${preparedBy || "-"}</td>
                                </tr>
                                <tr>
                                    <td class="label">Payment Type:</td>
                                    <td>${paymentMode || "Online"}</td>
                                    <td class="label">Receipt ID:</td>
                                    <td>${receiptReferenceId || "-"}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table class="line-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Particulars</th>
                                    <th class="num">Qty</th>
                                    <th class="num">Rate</th>
                                    <th class="num">Amount</th>
                                </tr>
                            </thead>
                            <tbody>${lineRows}</tbody>
                        </table>

                        <div class="amount-words"><span class="label">Amount (in words):</span> ${amountInWords || ""}</div>

                        <table class="summary-table">
                            <tbody>
                                <tr>
                                    <td class="label">Amount Receivable <span style="font-weight:400">| ${toReceiptAmount(totalAmount)}</span></td>
                                    <td class="label">Amount Received <span style="font-weight:400">| ${toReceiptAmount(totalAmount)}</span></td>
                                </tr>
                            </tbody>
                        </table>

                        <div class="powered">
                            <span>Powered by</span>
                            ${poweredByLogo ? `<img class="powered-logo" src="${poweredByLogo}" alt="Ceviliyar" />` : ``}
                        </div>
                    </div>
                </div>
            </body>
        </html>
    `;
};

export default PatientHistory;
